import { OWN_ATTRIBUTE_VALUE } from '~/config/settings/attribute.settings';
import { GlobalAction, RawUserPermission, UserPermissions } from '~/interfaces/permission.interface';

const CAN_ACCESS_SETTINGS = 'COMPANY_CONFIGURATION';
const CAN_ACCESS_EMPLOYEES = 'MANAGE_EMPLOYEES';
const CAN_ACCESS_USERS = 'MANAGE_USERS';
const CAN_ACCESS_USERS_AND_EMPLOYEES = 'MANAGE_USERS_AND_EMPLOYEES';
const CAN_ACCESS_ACTION_PLANNING_ALL = 'MANAGE_ACTIONS_FOR_ALL';
const CAN_ACCESS_ACTION_PLANNING_HIERARCHY = 'MANAGE_ACTIONS_UNDER_HIERARCHY';
const HAS_ACCESS_THROUGH_KW_WORKSPACE = 'KW_CUSTOM_ROLE';
const CAN_MANAGE_SURVEYS = 'MANAGE_SURVEYS';
const CAN_ACCESS_REPORTING = 'VIEW_REPORTING';
const CAN_ACCESS_CONVERSATIONS = 'ANONYMOUS_CONVERSATIONS';
const CAN_ACCESS_OPEN_COMMENTS = 'VIEW_OPEN_ENDED_COMMENTS';
const CAN_UPDATE_COMMENT_SENTIMENT = 'UPDATE_COMMENT_SENTIMENT';
const CAN_FILTER_SURVEY_COMMENTS = 'FILTER_SURVEY_COMMENTS';
const CAN_SEE_INPROGRESS_RESULTS = 'VIEW_INPROGRESS_SURVEY_RESULTS';
const CAN_UPDATE_COMPANIES = 'CAN_UPDATE_COMPANY';
const CAN_DELETE_COMPANIES = 'CAN_DELETE_COMPANY';
const CAN_MANAGE_CONTENT = 'CAN_MANAGE_CONTENT';
const CAN_VIEW_BENCHMARKS = 'VIEW_BENCHMARKS';
const CAN_MANAGE_AI_ASSISTANT = 'MANAGE_AI_ASSISTANT';
const CAN_ACCESS_AI_ASSISTANT = 'ACCESS_AI_ASSISTANT';
const CAN_RECEIVE_SUMMARY_EMAIL = 'RECEIVES_SUMMARY_EMAIL';
const CAN_SSO_DISABLED = 'SSO_DISABLED';

// WARNING: This is very brittle as it depends on specific database IDs.
const userActionIds = {
  [CAN_MANAGE_SURVEYS]: 1,
  [CAN_ACCESS_REPORTING]: 2,
  [CAN_ACCESS_CONVERSATIONS]: 3,
  [CAN_ACCESS_OPEN_COMMENTS]: 4,
  [CAN_UPDATE_COMMENT_SENTIMENT]: 5,
  [CAN_VIEW_BENCHMARKS]: 6,
  [CAN_FILTER_SURVEY_COMMENTS]: 7,
  [CAN_SEE_INPROGRESS_RESULTS]: 8,
};

// WARNING: This is extremely brittle and depends on the state of the database
// to map these id values to these string values. Change the backend so that it
// returns these string names in its response to requests globalActions on the user.
/**
 * Map global action database id values to string names (above).
 *
 * @example globalActions
 * [
 *   {
 *     "id": 13,
 *     "globalActionId": 1,
 *     "companyId": 5
 *   },
 *   {
 *     "id": 14,
 *     "globalActionId": 2,
 *     "companyId": 5
 *   },
 *   {
 *     "id": 15,
 *     "globalActionId": 3,
 *     "companyId": 5
 *   }
 * ]
 */
const globalActionIds = {
  [CAN_ACCESS_SETTINGS]: 1,
  [CAN_ACCESS_EMPLOYEES]: 2,
  [CAN_ACCESS_USERS]: 3,
  [CAN_ACCESS_ACTION_PLANNING_ALL]: 4,
  [CAN_ACCESS_ACTION_PLANNING_HIERARCHY]: 5,
  [CAN_ACCESS_USERS_AND_EMPLOYEES]: 6,
  [HAS_ACCESS_THROUGH_KW_WORKSPACE]: 7,
  [CAN_MANAGE_AI_ASSISTANT]: 8,
  [CAN_ACCESS_AI_ASSISTANT]: 9,
  [CAN_RECEIVE_SUMMARY_EMAIL]: 10,
  [CAN_SSO_DISABLED]: 11,
};

// WARNING: Using the translation keys as ids is brittle.
const globalActionTranslationKeys = {
  [CAN_ACCESS_SETTINGS]: 'permission.manageCompany',
  [CAN_ACCESS_EMPLOYEES]: 'permission.manageEmployees',
  [CAN_ACCESS_USERS]: 'permission.manageUsers',
  [CAN_ACCESS_ACTION_PLANNING_ALL]: 'permission.manageActionsForAll',
  [CAN_ACCESS_ACTION_PLANNING_HIERARCHY]: 'permission.manageActionsUnderHierarchy',
  [CAN_ACCESS_USERS_AND_EMPLOYEES]: 'permission.manageUsersAndEmployees',
};

/**
 * Go through app surveyTypePermissions returned by the server during
 * `fetchAccount` request and return a list of all unique userActions (the
 * string name of the action) which can be used to determine "global user
 * actions" for access to main menu items.
 *
 * @example surveyTypePermissions
 * [
 *   {
 *     "id": 34,
 *     "company": {
 *       "id": 5,
 *       "name": "WorkTango QA Production",
 *       "logoFileName": "https://pulse2-production-public-bucket.s3.ca-central-1.amazonaws.com/users/adda164f-4852-453d-b69f-e3acba5a555c_fedex2.jpg.jpeg",
 *       "topbarColorHex": "#ffffff",
 *       "preferredLanguage": {
 *         "id": "1",
 *         "langISOCode": "en-ca"
 *       }
 *     },
 *     "compareAttributes": [],
 *     "surveyTypes": [
 *       {
 *         "id": 26
 *       }
 *     ],
 *     "userActions": [
 *       {
 *         "id": 1,
 *         "action": "MANAGE_SURVEYS"
 *       },
 *       {
 *         "id": 2,
 *         "action": "VIEW_REPORTING"
 *       },
 *       {
 *         "id": 3,
 *         "action": "ANONYMOUS_CONVERSATIONS"
 *       },
 *       {
 *         "id": 4,
 *         "action": "VIEW_OPEN_ENDED_COMMENTS"
 *       }
 *     ],
 *     "canCompareToAllAttributes": true,
 *     "surveyTypeId": 26,
 *     "accessAll": true,
 *     "hierarchyBased": false
 *   }
 * ]
 */
const uniqueActionNamesFrom = (surveyTypePermissions: RawUserPermission[] = []) => {
  return surveyTypePermissions.reduce((uniqueActions, p) => {
    if (!p.userActions) return uniqueActions;

    const nextActions = p.userActions.reduce((actionNames, a) => {
      if (uniqueActions.includes(a.action)) return actionNames;

      return [...actionNames, a.action];
    }, [] as string[]);

    return [...uniqueActions, ...nextActions];
  }, [] as string[]);
};

/**
 * Go through all userPermissions (from `user.permissions` in object returned
 * during login) and return a list of unique permission names.
 *
 * @example userPermissions
 * [
 *   {
 *    "surveyTypeId": 3,
 *    "permissions": [
 *      "MANAGE_SURVEYS",
 *      "VIEW_REPORTING",
 *      "ANONYMOUS_CONVERSATIONS",
 *      "VIEW_OPEN_ENDED_COMMENTS"
 *    ],
 *   }
 * ]
 */
const uniqueUserPermissionNamesFrom = (userPermissions: { surveyTypeId: number; permissions: string[] }[] = []) => {
  return userPermissions.reduce((uniqueNames, p) => {
    return [...uniqueNames, ...p.permissions];
  }, [] as string[]);
};

/**
 * Transform data from "globalActions" + "userPermissions" coming from the
 * "user" object provided by the back-end API into front-end permissions object.
 *
 * @param {Array} globalActions - see example shape above `globalActionsIds`
 * @param {Array} surveyTypePermissions - see example shape above `uniqueActionNamesFrom()`
 * @param {Array} adminPermissions - set of string values for admin permissions
 * @return {Object}
 *
 * @example redux.permissions
 * {
 *   canSeeSettings: true,
 *   canSeeEmployees: true,
 *   canSeeUsers: true,
 *   canSeeConversations: true,
 *   canSeeComments: true,
 *   canUpdateCommentSentiment: true,
 *   canSeeReporting: true,
 *   canSeeSurveys: true,
 *   canSeeActionPlanningAll: false,
 *   canSeeActionPlanningHierarchy: true,
 *   canSeeActionPlanning: true,
 *
 *   surveyTypes: {
 *     1: {
 *       surveyTypeId: 1,
 *
 *       canSeeReporting: true,
 *       canSeeConversations: true,
 *       canSeeComments: true,
 *       canUpdateCommentSentiment: true,
 *       canManageSurveys: true,
 *
 *       canSeeAllResponseData: false,
 *       canSeeHierarchyResponseData: true,
 *       canSeeCustomResponseData: true,
 *       customResponseAttributes: {
 *         1: ['value1', 'value2'],
 *         2: ['value3'],
 *         3: ['value4', 'value5', 'value6']
 *       },
 *
 *       canFilterAllAttributes: false,
 *       filterAttributes: [1, 2, 3],
 *
 *       canCompareOrgWide: true,
 *       canCompareAllAttributes: false,
 *       compareAttributes: {
 *         1: ['value1', 'value2'],
 *         2: ['value3'],
 *         3: ['value4', 'value5', 'value6']
 *       }
 *     }
 *   }
 * }
 */
const permissionsFrom = (
  globalActions: GlobalAction[] = [],
  surveyTypePermissions: RawUserPermission[] = [],
  adminPermissions: string[] = [],
  mailingListPermission?: boolean,
) => {
  const uniqueActionNames = uniqueActionNamesFrom(surveyTypePermissions);

  // Root-level user permissions.
  const canSeeSettings = globalActions.some((a) => a.globalActionId === globalActionIds[CAN_ACCESS_SETTINGS]);
  const canSeeUsersAndEmployees = globalActions.some(
    (a) => a.globalActionId === globalActionIds[CAN_ACCESS_USERS_AND_EMPLOYEES],
  );
  const canSeeEmployees = canSeeUsersAndEmployees;
  const canSeeUsers = canSeeUsersAndEmployees;
  const canSeeActionPlanningAll = globalActions.some(
    (a) => a.globalActionId === globalActionIds[CAN_ACCESS_ACTION_PLANNING_ALL],
  );
  const canSeeActionPlanningHierarchy = globalActions.some(
    (a) => a.globalActionId === globalActionIds[CAN_ACCESS_ACTION_PLANNING_HIERARCHY],
  );

  const hasKWCustomRole = globalActions.some(
    (a) => a.globalActionId === globalActionIds[HAS_ACCESS_THROUGH_KW_WORKSPACE],
  );
  const canManageAiAssistant = globalActions.some((a) => a.globalActionId === globalActionIds[CAN_MANAGE_AI_ASSISTANT]);
  const canAccessAiAssistant = globalActions.some((a) => a.globalActionId === globalActionIds[CAN_ACCESS_AI_ASSISTANT]);
  const canReceiveSummaryEmail = globalActions.some(
    (a) => a.globalActionId === globalActionIds[CAN_RECEIVE_SUMMARY_EMAIL],
  );
  const ssoDisabled = globalActions.some((a) => a.globalActionId === globalActionIds[CAN_SSO_DISABLED]);

  const canSeeActionPlanning = canSeeActionPlanningAll || canSeeActionPlanningHierarchy;
  const canSeeConversations = uniqueActionNames.some((name) => name === CAN_ACCESS_CONVERSATIONS);
  const canSeeComments = uniqueActionNames.some((name) => name === CAN_ACCESS_OPEN_COMMENTS);
  const canUpdateCommentSentiment = uniqueActionNames.some((name) => name === CAN_UPDATE_COMMENT_SENTIMENT);
  const canSeeReporting = uniqueActionNames.some((name) => name === CAN_ACCESS_REPORTING);
  const canSeeSurveys = uniqueActionNames.some((name) => name === CAN_MANAGE_SURVEYS || name === CAN_ACCESS_REPORTING);
  const canUpdateCompanies = adminPermissions.includes(CAN_UPDATE_COMPANIES);
  const canDeleteCompanies = adminPermissions.includes(CAN_DELETE_COMPANIES);
  const canManageContent = adminPermissions.includes(CAN_MANAGE_CONTENT);
  // Inidividual survey type permission settings.
  const surveyTypes = surveyTypePermissions.reduce((acc, permission) => {
    if (!permission.surveyTypeId) return acc;

    // A dictionary of attribute values the user has permission to *analyze* where
    // the key is the database id for the attribute and the value is an array of
    // attribute values that are accessible for that attribute id.
    const customResponseAttributes =
      permission.attributes?.reduce(
        (acc, attr) => ({
          ...acc,
          [attr.id]: attr.values,
        }),
        {} as { [key: number]: string[] },
      ) || {};

    (permission.useUserAttributeValue || []).forEach((attributeId) => {
      if (!customResponseAttributes[attributeId]) {
        customResponseAttributes[attributeId] = [];
      }
      customResponseAttributes[attributeId].push(OWN_ATTRIBUTE_VALUE);
    });

    // A dictionary of attribute values the user has permission to *compare* where
    // the key is the database id for the attribute and the value is an array of
    // attribute values that are comparable for that attribute id.
    const compareAttributes =
      permission.compareAttributes?.reduce(
        (acc, attr) => ({
          ...acc,
          [attr.id]: attr.values,
        }),
        {} as { [key: number]: string[] },
      ) || {};

    (permission.useUserAttributeValueCompare || []).forEach((attributeId) => {
      if (!compareAttributes[attributeId]) {
        compareAttributes[attributeId] = [];
      }
      compareAttributes[attributeId].push(OWN_ATTRIBUTE_VALUE);
    });

    // An array of attribute database ids that are available from the filter menu.
    const filterAttributes = permission.visibleAttributes?.map((attr) => attr.attributeId) || [];

    return {
      ...acc,
      [permission?.surveyTypeId]: {
        surveyTypeId: permission?.surveyTypeId,

        canManageSurveys: permission?.userActions?.some((ua) => ua?.id === userActionIds[CAN_MANAGE_SURVEYS]),
        canSeeReporting: permission?.userActions?.some((ua) => ua?.id === userActionIds[CAN_ACCESS_REPORTING]),
        canSeeConversations: permission?.userActions?.some((ua) => ua?.id === userActionIds[CAN_ACCESS_CONVERSATIONS]),
        canSeeComments: permission?.userActions?.some((ua) => ua?.id === userActionIds[CAN_ACCESS_OPEN_COMMENTS]),
        canUpdateCommentSentiment: permission?.userActions?.some(
          (ua) => ua?.id === userActionIds[CAN_UPDATE_COMMENT_SENTIMENT],
        ),
        canFilterSurveyComments: permission?.userActions?.some(
          (ua) => ua?.id === userActionIds[CAN_FILTER_SURVEY_COMMENTS],
        ),
        canViewBenchmarks: permission?.userActions?.some((ua) => ua?.id === userActionIds[CAN_VIEW_BENCHMARKS]),
        canSeeInProgressResults: permission?.userActions?.some(
          (ua) => ua?.id === userActionIds[CAN_SEE_INPROGRESS_RESULTS],
        ),

        // What survey response *data* the user is allowed to see.
        canSeeAllResponseData: !!permission?.accessAll,
        canSeeHierarchyResponseData: !!permission?.hierarchyBased,
        canSeeCustomResponseData: !!permission?.attributeBased && !!permission?.attributes?.length,
        customResponseAttributes, // custom attributes/values for canSeeCustomResponseData (ignored if canSeeAllResponseData)
        canSeeOtherHierarchiesResponseData: !!permission?.otherHierarchies?.length,
        otherHierarchiesAttributeValues: permission?.otherHierarchies || [],
        useUserAttributeValue: permission?.useUserAttributeValue, // custom attribute access based on user's attribute value

        // What *filters* are available for the user to choose from the menu when aggregating surveys.
        canFilterAllAttributes: !!permission?.allAttributesVisible,
        filterAttributes, // list of attribute ids that can be used as filters

        // What survey response data the user is allowed to *compare*.
        // currently database/backend use mismatched column name canCompareToAllAttributes to store this value
        canCompareOrgWide: !!permission?.accessAll || !!permission?.canCompareToAllAttributes,
        // only people canSeeAllResponseData can compare to all attributes
        canCompareAllAttributes: !!permission?.accessAll,
        compareAttributes, // custom attribute/values that can be compared
        useUserAttributeValueCompare: permission?.useUserAttributeValueCompare, // compare attribute based on user's own attribute value
      },
    };
  }, {});

  // WARNING: This is a bit of a hack. This is to create a list of only those
  // "global" permissions that can be configured on the "manage user" page. Not
  // all permissions are able to be configured by non-WorkTango users, so we
  // need to distinguish which ones can be from which ones cannot be.
  const globalActionNames: string[] = [];
  if (canSeeSettings) globalActionNames.push(globalActionTranslationKeys[CAN_ACCESS_SETTINGS]);
  if (canSeeUsersAndEmployees) globalActionNames.push(globalActionTranslationKeys[CAN_ACCESS_USERS_AND_EMPLOYEES]);
  if (canSeeActionPlanningAll) globalActionNames.push(globalActionTranslationKeys[CAN_ACCESS_ACTION_PLANNING_ALL]);
  if (canSeeActionPlanningHierarchy)
    globalActionNames.push(globalActionTranslationKeys[CAN_ACCESS_ACTION_PLANNING_HIERARCHY]);

  const canManageMailingList = mailingListPermission;

  const permissions = {
    canSeeSettings,
    canSeeEmployees,
    canSeeUsers,
    canSeeUsersAndEmployees,
    canSeeConversations,
    canSeeComments,
    canUpdateCommentSentiment,
    canSeeReporting,
    canSeeSurveys,
    canSeeActionPlanning,
    canSeeActionPlanningAll,
    canSeeActionPlanningHierarchy,
    canUpdateCompanies,
    canDeleteCompanies,
    canManageContent,
    surveyTypes,
    globalActionNames,
    hasKWCustomRole,
    canManageMailingList,
    canManageAiAssistant,
    canAccessAiAssistant,
    canReceiveSummaryEmail,
    ssoDisabled,
  };

  return permissions;
};

/**
 * Check each selected attributes if their values contain OWN_ATTRIBUTE_VALUE, then
 * 1) remove them from selected values
 * 2) push them into one useUserAttributes array
 */
const parseUseUserAttributes = (selectedAttributes: { [id: number]: string[] } = {}) => {
  const parsedResult = Object.keys(selectedAttributes).reduce(
    (parsedAttributes, attrId) => {
      const values = selectedAttributes[attrId];
      const valuesWithoutUserValue = values.filter((value) => value !== OWN_ATTRIBUTE_VALUE);
      const attrs = {
        ...parsedAttributes.attributes,
        [attrId]: valuesWithoutUserValue,
      };
      const userAttributeValue =
        valuesWithoutUserValue.length !== values.length
          ? [...parsedAttributes.useUserAttributeValue, Number(attrId)]
          : parsedAttributes.useUserAttributeValue;
      return { attributes: attrs, useUserAttributeValue: userAttributeValue };
    },
    { attributes: {}, useUserAttributeValue: [] } as {
      attributes: { [id: number]: string[] };
      useUserAttributeValue: number[];
    },
  );

  const { attributes, useUserAttributeValue } = parsedResult;

  return {
    selectedAttributes: attributes,
    useUserAttributes: useUserAttributeValue.length ? useUserAttributeValue : null,
  };
};

/**
 * Convert the front-end Redux permissions shape to something that is more
 * friendly for the backend (e.g., using database ids).
 *
 * NOTE: The output does not include any userId data, only the permissions. This
 * is because it may be used for one, or for many, users at once. The concern of
 * which users to apply these permissions to is outside the scope of converting
 * the permissions themselves.
 *
 * @see /src/behaviors/user.actions.js
 *
 * @example output
 * {
 *   globalActionIds: [1, 2, 3],
 *   surveyTypes: [
 *     {
 *       surveyTypeId: 123,
 *       canCompareToAllAttributes: false,
 *       allAttributesVisible: true,
 *       accessAll: true,
 *       hierarchyBased: false,
 *       attributeBased: false,
 *       userActionIds: [1, 2, 3],
 *       customResponseAttributes: {
 *         1: ['val1', 'val2'],
 *         2: ['val3']
 *       },
 *       compareAttributes: {
 *         1: ['val1', 'val2'],
 *         2: ['val3'],
 *         3: ['val4', 'val5', 'val7']
 *       },
 *       filterAttributes: [1, 2, 3]
 *     }
 *   ]
 * }
 */
const permissionsToIds = (permissions: UserPermissions, managerEmployeeIdAttrId) => {
  const newGlobalActionIds: number[] = [];

  if (permissions?.canSeeSettings) newGlobalActionIds.push(globalActionIds[CAN_ACCESS_SETTINGS]);
  if (permissions?.canSeeEmployees) newGlobalActionIds.push(globalActionIds[CAN_ACCESS_EMPLOYEES]);
  if (permissions?.canSeeUsers) newGlobalActionIds.push(globalActionIds[CAN_ACCESS_USERS]);
  if (permissions?.canSeeActionPlanningAll) newGlobalActionIds.push(globalActionIds[CAN_ACCESS_ACTION_PLANNING_ALL]);
  if (permissions?.canSeeActionPlanningHierarchy)
    newGlobalActionIds.push(globalActionIds[CAN_ACCESS_ACTION_PLANNING_HIERARCHY]);
  if (permissions?.canSeeUsersAndEmployees) newGlobalActionIds.push(globalActionIds[CAN_ACCESS_USERS_AND_EMPLOYEES]);
  if (permissions?.canManageAiAssistant) newGlobalActionIds.push(globalActionIds[CAN_MANAGE_AI_ASSISTANT]);
  if (permissions?.canAccessAiAssistant) newGlobalActionIds.push(globalActionIds[CAN_ACCESS_AI_ASSISTANT]);
  if (permissions?.canReceiveSummaryEmail) newGlobalActionIds.push(globalActionIds[CAN_RECEIVE_SUMMARY_EMAIL]);
  if (permissions?.ssoDisabled) newGlobalActionIds.push(globalActionIds[CAN_SSO_DISABLED]);
  if (permissions?.hasKWCustomRole) newGlobalActionIds.push(globalActionIds[HAS_ACCESS_THROUGH_KW_WORKSPACE]);

  const surveyTypes = Object.values(permissions?.surveyTypes).map((permission) => {
    const newUserActionIds: number[] = [];

    if (permission?.canManageSurveys) newUserActionIds.push(userActionIds[CAN_MANAGE_SURVEYS]);
    if (permission?.canSeeReporting) newUserActionIds.push(userActionIds[CAN_ACCESS_REPORTING]);
    if (permission?.canSeeConversations) newUserActionIds.push(userActionIds[CAN_ACCESS_CONVERSATIONS]);
    if (permission?.canSeeComments) newUserActionIds.push(userActionIds[CAN_ACCESS_OPEN_COMMENTS]);
    if (permission?.canUpdateCommentSentiment) newUserActionIds.push(userActionIds[CAN_UPDATE_COMMENT_SENTIMENT]);
    if (permission?.canFilterSurveyComments) newUserActionIds.push(userActionIds[CAN_FILTER_SURVEY_COMMENTS]);
    if (permission?.canViewBenchmarks) newUserActionIds.push(userActionIds[CAN_VIEW_BENCHMARKS]);
    if (permission?.canSeeInProgressResults) newUserActionIds.push(userActionIds[CAN_SEE_INPROGRESS_RESULTS]);

    const { selectedAttributes: attributes, useUserAttributes: useUserAttributeValue } = parseUseUserAttributes(
      permission?.customResponseAttributes,
    );

    const { selectedAttributes: compareAttributes, useUserAttributes: useUserAttributeValueCompare } =
      parseUseUserAttributes(permission?.compareAttributes);

    const combinedAttribtes = permission?.canSeeOtherHierarchiesResponseData
      ? { ...attributes, [managerEmployeeIdAttrId]: permission?.otherHierarchiesAttributeValues }
      : attributes;

    return {
      surveyTypeId: permission?.surveyTypeId,
      canCompareToAllAttributes: permission?.canCompareOrgWide,
      allAttributesVisible: permission?.canFilterAllAttributes,
      accessAll: permission?.canSeeAllResponseData,
      hierarchyBased: permission?.canSeeHierarchyResponseData,
      attributeBased: permission?.canSeeCustomResponseData || permission?.canSeeOtherHierarchiesResponseData,
      attributes: combinedAttribtes,
      compareAttributes,
      filterAttributes: permission?.filterAttributes,
      userActionIds: newUserActionIds,
      useUserAttributeValue,
      useUserAttributeValueCompare,
    };
  });

  return {
    globalActionIds: newGlobalActionIds,
    surveyTypes,
  };
};

export { permissionsFrom, permissionsToIds, uniqueActionNamesFrom, uniqueUserPermissionNamesFrom };
